import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Zap, Star, Crown, Coins, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import api from "../lib/apiClient";
import { ProBadge } from "../components/shared/ProBadge";

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

// Tiers — mirror backend/core.py SUBSCRIPTION_TIERS. Quarterly credits
// is the marketed number (80/200/600) — that's what gets displayed.
const TIERS = [
  { name: "baby_steps", price: 0, qCredits: 20, qPrice: 0,
    description: "Open your FREE profile", icon: User, color: "pink",
    isSignupBonus: true, channelPerk: "1 channel",
    videoPerk: "Watch only", sponsorPerk: "2 sponsor links" },
  { name: "beginner", price: 2, qCredits: 80, qPrice: 6,
    description: "Start creating", icon: Zap, color: "blue",
    channelPerk: "1 channel",
    videoPerk: "Videos up to 6 min", sponsorPerk: "2 sponsor links" },
  { name: "experimenter", price: 4, qCredits: 200, qPrice: 12,
    description: "Regular creator", icon: Star, color: "emerald",
    channelPerk: "1 channel",
    videoPerk: "Videos up to 6 min", sponsorPerk: "2 sponsor links" },
  { name: "producer", price: 8, qCredits: 600, qPrice: 24,
    description: "Serious producer · launch unlimited anonymous channels",
    icon: Crown, color: "amber", channelPerk: "UNLIMITED channels",
    videoPerk: "Videos up to 9 min",
    sponsorPerk: "4 sponsor links" },
];

const colorClasses = {
  pink:    { bg: "from-pink-500/20 to-pink-600/5",   border: "border-pink-500/40", icon: "text-pink-400",   button: "bg-pink-600 hover:bg-pink-500" },
  blue:    { bg: "from-blue-500/20 to-blue-600/5",   border: "border-blue-500/40", icon: "text-blue-400",   button: "bg-blue-600 hover:bg-blue-500" },
  emerald: { bg: "from-emerald-500/20 to-emerald-600/5", border: "border-emerald-500/40", icon: "text-emerald-400", button: "bg-emerald-600 hover:bg-emerald-500" },
  amber:   { bg: "from-amber-500/20 to-amber-600/5", border: "border-amber-500/40", icon: "text-amber-400",  button: "bg-amber-600 hover:bg-amber-500" },
};

const PricingPage = () => {
  const { user } = useAuth();
  const [subscribing, setSubscribing] = useState(null);
  const [stripePackages, setStripePackages] = useState([]);
  const [stripeLoading, setStripeLoading] = useState(null);
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const _nav = navigate;

  useEffect(() => {
    api.get(`/payments/packages`).then(r => setStripePackages(r.data.topup || [])).catch(() => {});
  }, []);

  const hasSubscription = user && user.subscription_tier && user.subscription_tier !== "free";
  const isProducer = user?.subscription_tier === "producer";

  // Producer top-up rendered FIRST per Jun 7 spec. For other subscribed
  // tiers (Beginner / Experimenter) the top-up still appears, but below
  // the tier grid. Anonymous + free users never see top-up — they must
  // subscribe first.
  const showTopUp = hasSubscription;

  const handleSubscribe = async (tier) => {
    if (!user) { toast.error("Please sign in first"); return; }
    // Auto-renew is now the ONLY mode (Jun 7 spec).
    setSubscribing(tier);
    try {
      let referrer_username;
      try {
        const raw = localStorage.getItem("globaleyes_referrer");
        if (raw) {
          const parsed = JSON.parse(raw);
          const ageMs = Date.now() - (parsed.ts || 0);
          if (parsed.username && tier === "producer" && ageMs <= NINETY_DAYS_MS) {
            referrer_username = parsed.username;
          }
        }
      } catch (_err) { /* ignore */ }
      const payload = referrer_username
        ? { tier, referrer_username, origin_url: window.location.origin }
        : { tier, origin_url: window.location.origin };
      const res = await api.post(`/payments/subscribe/recurring/checkout`, payload);
      if (referrer_username) {
        try { localStorage.setItem("globaleyes_referrer_pending", "1"); } catch (_e) { /* ignore */ }
      }
      window.location.href = res.data.url;
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start subscription checkout");
    }
    setSubscribing(null);
  };

  const handleStripeTopUp = async (packageId) => {
    if (!user) { toast.error("Please sign in first"); return; }
    setStripeLoading(packageId);
    try {
      const res = await api.post(`/payments/topup/checkout`, {
        package_id: packageId, origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setStripeLoading(null);
    }
  };

  const TierCard = ({ tier }) => {
    const palette = colorClasses[tier.color];
    const Icon = tier.icon;
    const isCurrent = user?.subscription_tier === tier.name;
    const isFree = tier.isSignupBonus;
    return (
      <Card className={`bg-gradient-to-b ${palette.bg} ${palette.border} relative overflow-hidden flex flex-col`}>
        <CardContent className="p-3 flex-1 flex flex-col text-center">
          <Icon className={`h-7 w-7 ${palette.icon} mx-auto mb-1.5`} />
          <h3 className="text-base font-bold text-white capitalize leading-tight">
            {tier.name.replace("_", " ")}
          </h3>
          <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{tier.description}</p>

          {/* Price */}
          <div className="my-2">
            {isFree ? (
              <div className="text-2xl font-bold text-white">FREE</div>
            ) : (
              <>
                <div className="text-2xl font-bold text-white leading-none">
                  ${tier.price}<span className="text-sm font-normal text-gray-400">/mo</span>
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  ${tier.qPrice} billed quarterly
                </div>
              </>
            )}
          </div>

          {/* YOU RECEIVE block — Jun 7 spec: stacked vertically with amount on its own line */}
          <div className="mb-2 py-1.5 border-y border-white/10">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 leading-tight">You</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 leading-tight">Receive</div>
            <div className={`text-lg font-bold ${palette.icon} mt-0.5 tabular-nums leading-tight`}>
              {tier.qCredits} <span className="text-[10px] font-normal text-gray-400">cr/qtr</span>
            </div>
          </div>

          <div className="text-[10px] text-gray-300 mb-1 space-y-0.5">
            <div><strong className="text-white">{tier.channelPerk}</strong></div>
            {tier.videoPerk && (
              <div className={`flex items-start gap-1 ${tier.name === "producer" ? "text-amber-300" : "text-gray-400"}`}>
                {tier.name === "producer" && <ProBadge unlocked className="mt-0.5 shrink-0" />}
                <span>{tier.videoPerk}</span>
              </div>
            )}
            {tier.sponsorPerk && (
              <div className={`flex items-start gap-1 ${tier.name === "producer" ? "text-amber-300" : "text-gray-400"}`}>
                {tier.name === "producer" && <ProBadge unlocked className="mt-0.5 shrink-0" />}
                <span>{tier.sponsorPerk}</span>
              </div>
            )}
          </div>

          {/* CTA */}
          {isFree ? (
            user ? (
              <div className="text-[10px] text-emerald-300 mt-auto py-1.5">Active</div>
            ) : (
              <Button onClick={() => navigate("/")} size="sm" className={`${palette.button} text-white text-xs mt-auto`}>Sign up</Button>
            )
          ) : isCurrent ? (
            <div className="text-[10px] text-emerald-300 mt-auto py-1.5 font-semibold">✓ Your plan</div>
          ) : (
            <Button
              onClick={() => handleSubscribe(tier.name)}
              disabled={!!subscribing}
              size="sm"
              data-testid={`subscribe-${tier.name}`}
              className={`${palette.button} text-white text-xs mt-auto`}
            >
              {subscribing === tier.name ? "Loading…" : "Subscribe"}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  const TopUpBox = ({ compact = false }) => (
    <Card className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border-emerald-500/40" data-testid="topup-box">
      <CardContent className={compact ? "p-3" : "p-4"}>
        <div className="flex items-center gap-2 mb-2">
          <Coins className="h-5 w-5 text-emerald-400" />
          <h2 className={`${compact ? "text-base" : "text-lg"} font-bold text-white`}>
            Top-up Your Credits
          </h2>
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-400/60 ml-auto" />
        </div>
        <div className={`grid ${stripePackages.length >= 3 ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-5" : "grid-cols-2"} gap-2`}>
          {stripePackages.map(pkg => (
            <button
              key={pkg.id}
              onClick={() => handleStripeTopUp(pkg.id)}
              disabled={!!stripeLoading}
              data-testid={`topup-${pkg.id}`}
              className="bg-gray-900/60 hover:bg-emerald-600/20 border border-emerald-500/30 hover:border-emerald-400 rounded-lg p-2 text-center transition-all disabled:opacity-50 group"
            >
              <div className="text-emerald-300 font-bold text-base">{pkg.credits} cr</div>
              <div className="text-gray-300 text-[10px]">${pkg.amount_usd}</div>
              {stripeLoading === pkg.id && (
                <div className="text-[9px] text-emerald-400 mt-0.5">Loading…</div>
              )}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div
      className="min-h-[calc(100dvh-4rem)] sm:h-[calc(100dvh-4rem)] bg-gradient-to-b from-gray-950 to-black overflow-y-auto sm:overflow-hidden flex flex-col"
      data-testid="pricing-page"
    >
      <div className="container mx-auto px-3 py-3 sm:px-4 sm:py-4 pb-32 sm:pb-4 flex-1 flex flex-col min-h-0">
        {/* Header — compact */}
        <div className="text-center mb-3 sm:mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-white">Choose Your Plan</h1>
          <p className="text-amber-400/80 text-[11px] sm:text-xs">
            All plans CREDITED QUARTERLY · Credits evaporate after 90 days
          </p>
        </div>

        {/* Producer top-up FIRST (Jun 7 spec) */}
        {isProducer && showTopUp && (
          <div className="mb-3">
            <TopUpBox />
          </div>
        )}

        {/* Tier grid — 4 cards, equal width, fills remaining vertical space */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 flex-1 min-h-0">
          {TIERS.map(tier => <TierCard key={tier.name} tier={tier} />)}
        </div>

        {/* Non-Producer top-up below the grid (only when subscribed) */}
        {showTopUp && !isProducer && (
          <div className="mt-3">
            <TopUpBox compact />
          </div>
        )}

        {!hasSubscription && (
          <p className="text-center text-gray-500 text-[10px] mt-2">
            Top-up packages unlock after you choose a plan
          </p>
        )}
      </div>
    </div>
  );
};

export default PricingPage;
